/**
 * Shared prose building blocks for guide articles — kept separate from
 * GuideLayout so individual guide pages can import just what they use.
 *
 * Structure deliberately mirrors ForOrganisations.jsx's `Section`/`FaqItem`:
 * every section is full-bleed (edge to edge, page gutter or a rounded
 * tinted panel) with its own centred max-w-3xl reading column inside —
 * not one shared wrapper around the whole article — so a guide reads as
 * the same alternating white/green/amber bars as the for-organisations page.
 */

/** A titled prose section within a guide article. `tinted` puts it on a full-bleed ground panel. */
export const GuideSection = ({ eyebrow, title, tinted = false, id, children }) => (
  <section id={id} className={tinted ? 'mx-2.5 mb-2.5 rounded-2xl bg-ground px-7 py-12 lg:px-12' : 'px-4 sm:px-7 lg:px-12 py-12'}>
    <div className="max-w-3xl mx-auto space-y-5">
      {eyebrow && <p className="pd-eyebrow">{eyebrow}</p>}
      {title && (
        <h2 className="font-heading text-[28px] sm:text-[34px] font-bold tracking-[-0.035em] leading-[1.05] text-ink">
          {title}
        </h2>
      )}
      <div className="space-y-4 text-ink-70 leading-relaxed">{children}</div>
    </div>
  </section>
);

/** FAQ item — white card, for use inside a tinted (green) GuideSection so it stands out the way it does on ForOrganisations. */
export const GuideFaqItem = ({ q, a }) => (
  <details className="group rounded-lg bg-surface px-5 py-4 [&_summary::-webkit-details-marker]:hidden">
    <summary className="cursor-pointer flex items-start justify-between gap-3 text-ink font-semibold list-none min-h-[28px]">
      <span>{q}</span>
      <span className="text-ink-55 group-open:rotate-45 transition-transform duration-state text-xl leading-none flex-shrink-0" aria-hidden="true">
        +
      </span>
    </summary>
    <p className="mt-3 text-[15px] text-ink-70 leading-relaxed">{a}</p>
  </details>
);

/** A link out to an authoritative source (ICO, legislation.gov.uk, gov.uk, etc.). */
export const SourceLink = ({ href, children }) => (
  <a href={href} target="_blank" rel="noreferrer" className="text-brand underline hover:opacity-90">
    {children}
  </a>
);

/** A bulleted checklist of what to include in a request — same visual weight as GuideSection body. */
export const GuideChecklist = ({ items }) => (
  <ul className="space-y-2 list-disc pl-5">
    {items.map((item, i) => (
      <li key={i}>{item}</li>
    ))}
  </ul>
);

/**
 * A short, standalone callout for a specific warning or exception — amber,
 * not an error. Full-bleed with its own reading column (like GuideSection's
 * plain variant) so it can sit directly between sections at the top level
 * of a guide, not just nested inside one.
 */
export const GuideNote = ({ children }) => (
  <div className="px-4 sm:px-7 lg:px-12 py-2">
    <div className="max-w-3xl mx-auto rounded-lg bg-amber-wash text-ink px-5 py-4">
      <div className="space-y-2 text-[15px] leading-relaxed">{children}</div>
    </div>
  </div>
);
